import React from 'react';
import { PrismicLink } from 'components/PrismicLink';
import { LrButton } from 'components/LANDR/LrButton';
import { Grid, GridRow, GridCol } from 'components/UI/Grid';
import { PrismicHTML, PrismicFluidImage } from 'components/Prismic';
import './CallToAction.scss';
import { useAuthentication } from 'components/Authentication';
import { PrismicExternalLink, PrismicInternalLink } from 'components/Slices/types';
import { AppContextType } from 'context/AppContext';
import { PrismicLinkTypes, SupportedPrismicSlicesEnum } from '../enums';
import {
    PrismicRichTextFieldType,
    PrismicKeyTextFieldType,
    PrismicTitleFieldType,
    PrismicLinkFieldType,
    SliceContext,
    PrismicImageField,
    PrismicFluidImageField,
} from '../types';

type CallToActionSlice = {
    type: SupportedPrismicSlicesEnum.cta;
    primary: {
        call_to_action_theme: 'light' | 'dark';
        call_to_action_title?: PrismicTitleFieldType;
        call_to_action_description?: PrismicRichTextFieldType;
        call_to_action_label: PrismicKeyTextFieldType;
        call_to_action_link: PrismicLinkFieldType;
        call_to_action_background_image: PrismicImageField;
        call_to_action_background_imageSharp: PrismicFluidImageField;
    };
};

type CallToActionProps = {
    slice: CallToActionSlice;
    sliceContext: SliceContext;
    appContext: AppContextType;
};

/**
 * All signup links returned by Prismic are not optimized for Auth0 (send to the main app with query params)
 * this fn will return a new Auth0 compatible signup link or return the same link if it's not a signup link
 * @param ctaLink - original CTA link from prismic
 */
const replaceCtaIfSignup = (ctaLink: PrismicLinkFieldType): PrismicLinkFieldType => {
    let { signupUrl } = useAuthentication();

    const isPrismicSignupExternalLink = (link: PrismicLinkFieldType) => {
        return (
            link && link._linkType === PrismicLinkTypes.external && (link as PrismicExternalLink).url.includes('signup')
        );
    };
    const isPrismicSignupInternalLink = (link: PrismicLinkFieldType) => {
        return (
            link &&
            link._linkType === PrismicLinkTypes.internal &&
            (link as PrismicInternalLink)._meta.uid.toLowerCase().includes('signup')
        );
    };

    if (isPrismicSignupExternalLink(ctaLink) || isPrismicSignupInternalLink(ctaLink)) {
        return {
            _linkType: PrismicLinkTypes.external,
            url: signupUrl,
            target: '_self',
        };
    }

    return ctaLink;
};

const CallToAction = ({ slice, sliceContext }: CallToActionProps) => {
    const { primary } = slice;

    let ctaLink: PrismicLinkFieldType = replaceCtaIfSignup(primary.call_to_action_link);

    return (
        <div className="CallToAction" data-theme={primary.call_to_action_theme}>
            <Grid>
                <GridRow horizontalAlign={'h-center'} verticalAlign={'v-center'}>
                    <GridCol colSize={`12`} offsetSize={`0`} mobileColSize={'12'}>
                        {(primary.call_to_action_title || primary.call_to_action_description) && (
                            <div className="CallToAction-content">
                                <PrismicHTML content={primary.call_to_action_title} className="CallToAction-title" />
                                <PrismicHTML
                                    content={primary.call_to_action_description}
                                    className="CallToAction-description"
                                />
                            </div>
                        )}
                        {ctaLink && (
                            <PrismicLink link={ctaLink} className="CallToAction-cta" slice={sliceContext}>
                                <LrButton variant="primary">{primary.call_to_action_label}</LrButton>
                            </PrismicLink>
                        )}
                    </GridCol>
                </GridRow>
            </Grid>
            <PrismicFluidImage
                className="CallToAction-background"
                image={primary.call_to_action_background_imageSharp}
                fallback={primary.call_to_action_background_image}
            />
        </div>
    );
};

export { CallToAction, CallToActionProps };
